<template>
  <li
    class="cart-item cart-row"
    :class="{ 'cart-item--updating': updatingItem }"
  >
    <div class="flex">
      <div class="cart-item__image-wrapper">
        <a
          :href="item.url"
          class="cart-item__image block"
          :class="{ 'cart-item__image--png': item.image.includes('.png') }"
        >
          <img
            :src="image"
            :alt="item.featured_image.alt"
            width="95"
            height="95"
          >
        </a>
        <button
          class="cart-item__delete"
          @click="removeItemFromCart"
        >
          {{ strings.remove }}
          <DeleteIcon />
        </button>
      </div>
      <div class="cart-item__info">
        <h3 class="cart-item__title">
          {{ item.product_title }}
        </h3>
        <ul class="cart-item__information">
          <li
            v-for="option in optionsWithValues"
            :key="`${item.id}-${option.name}`"
            class="cart-item__information-item"
          >
            {{ option.name }}: {{ option.value }}
          </li>
          <li
            v-if="isSubscriptionItem"
            class="cart-item__information-item"
          >
            Subscription: {{ subscriptionFrequency }}
          </li>
          <li
            v-if="isCustomisableBundle"
            class="cart-item__information-item cart-item__information-item--bundle-contents"
            v-html="bundleText"
          />
        </ul>
      </div>
    </div>
    <p class="cart-item__information-item cart-item__information-item--price">
      <s v-if="item.original_price != item.final_price">
        {{ originalPrice }}
      </s>
      {{ finalPrice }}
    </p>
    <div
      class="cart-item__quantity"
    >
      <button
        class="quantity-buttons__button"
        @click="decreaseQuantity"
      >
        <MinusCircleFilled />
      </button>
      <span class="quantity-buttons__amount">
        {{ item.quantity }}
      </span>
      <button
        class="quantity-buttons__button"
        :disabled="!customerCanIncreaseQuantity"
        @click="increaseQuantity"
      >
        <PlusCircleFilled />
      </button>
    </div>
    <p class="cart-item__information-item cart-item__information-item--price">
      <s v-if="item.original_line_price != item.final_line_price">
        {{ originalLinePrice }}
      </s>
      {{ finalLinePrice }}
    </p>
  </li>
</template>

<script>
import { useCartStore } from '../../pinia/CartStore'
import { mapState, mapActions } from 'pinia'

import { productPrice } from '../../helpers/product'
import { getSizedImageUrl } from '@shopify/theme-images'

import DeleteIcon from '../../icons/delete-icon'
import PlusCircleFilled from '../../icons/plus-circle-filled'
import MinusCircleFilled from '../../icons/minus-circle-filled'

export default {
  components: {
    DeleteIcon,
    PlusCircleFilled,
    MinusCircleFilled
  },
  props: {
    item: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      strings: { ...window.theme.cart.strings },
      bundles: [ ...window.theme.bundles ],
      purchaseLimitProducts: [ ...window.theme.cart.purchaseLimitCollectionProducts ],
      updatingItem: false
    }
  },
  computed: {
    ...mapState (useCartStore, [
      'bundleItems'
    ]),
    image () {
      return getSizedImageUrl(this.item.featured_image.url, '190x')
    },
    originalLinePrice () {
      return productPrice(this.item.original_line_price)
    },
    finalLinePrice () {
      return this.isCustomisableBundle ? productPrice(this.bundlePrice * this.item.quantity) : productPrice(this.item.final_line_price)
    },
    originalPrice () {
      return productPrice(this.item.original_price)
    },
    finalPrice () {
      return this.isCustomisableBundle ? productPrice(this.bundlePrice * this.item.quantity) : productPrice(this.item.final_price)
    },
    itemHasVariants () {
      return !this.item.product_has_only_default_variant && (this.item.options_with_values[0].name !== 'Options')
    },
    optionsWithValues () {
      return this.itemHasVariants ? { ...this.item.options_with_values } : []
    },
    isSubscriptionItem () {
      return this.item.selling_plan_allocation
    },
    subscriptionFrequency () {
      return this.isSubscriptionItem ? this.item.selling_plan_allocation.selling_plan.options[0].value : null
    },
    productHasPurchaseLimit () {
      return !!this.purchaseLimitProducts.find(product => product.handle === this.item.handle)
    },
    purchaseLimit () {
      return this.productHasPurchaseLimit ? this.purchaseLimitProducts.find(product => product.handle === this.item.handle).purchaseLimit : 999999
    },
    customerHasReachedOrExceededQuantityLimit () {
      if (!this.productHasPurchaseLimit) return false
      return this.item.quantity >= this.purchaseLimit
    },
    customerHasExceededLimit () {
      if (!this.productHasPurchaseLimit) return false
      return this.item.quantity > this.purchaseLimit
    },
    customerCanIncreaseQuantity () {
      return !this.customerHasReachedOrExceededQuantityLimit
    },
    isCustomisableBundle () {
      return !!this.item.properties?._bundle_key || !!this.bundles.find(bundle => bundle.product.handle === this.item.handle)
    },
    isMetafieldsBundle () {
      return !!this.item.properties._bundle_key
    },
    bundleText () {
      if (!this.isCustomisableBundle) return null
      let text = ''
      for (const [key, value] of Object.entries(this.item.properties)) {
        if (key.startsWith('_bundle')) {
          continue
        }
        if (!(typeof value === 'string' || value instanceof String)) {
          continue
        }
        const productTitle = value.replace(/in-game-/g, '')
          .replace(/-solo/g, '')
          .replace(/-sachet/g, '')
          .replace(/-shaker/g, '')
          .replace(/sneak-/g, '')
          .replace(/-7g/g, '')
          .replace(/-210g-uk/g, '')
          .replace(/-5-x-10g/g, '')
          .replace(/-/g, ' ')
          .replace(/og/g, 'OG')
        text = `${text}<span>${value.includes('5-x-10') ? '5 X ' : ''}${productTitle}${value.includes('5-x-10') ? '' : ` ${key.replace(/^_/, '').split(' ')[0].toLowerCase()}`}</span>`
      }
      return text
    },
    matchingBundleItems () {
      if (!this.isCustomisableBundle) return []
      return this.bundleItems.filter(item => { return (item.properties.bundle === this.item.handle) })
    },
    matchingBundleItemsWithQuantites () {
      const items = []
      if (!this.isMetafieldsBundle) {
        for (const [key, value] of Object.entries(this.item.properties)) {
          const matchingCartItem = this.matchingBundleItems.find(item => item.handle === value)
          if (items.find(addedItem => addedItem.key === matchingCartItem.key)) {
            const indexOfItemInArray = items.findIndex(addedItem => addedItem.key === matchingCartItem.key)
            items[indexOfItemInArray].bundleQuantity = items[indexOfItemInArray].bundleQuantity + 1
          } else {
            items.push(
                {
                  key: matchingCartItem.key,
                  bundleQuantity: 1,
                  quantity: matchingCartItem.quantity
                }
            )
          }
        }
      }
      return items
    },
    bundleRemovePayload () {
      const payload = {
        updates: {
          [this.item.key]: 0
        }
      }
      this.matchingBundleItemsWithQuantites.forEach(item => {
        const newQuantity = item.quantity - (this.item.quantity * item.bundleQuantity)
        payload.updates[item.key] = newQuantity
      })
      return payload
    },
    bundleIncreasePayload () {
      const payload = {
        updates: {
          [this.item.key]: this.item.quantity + 1
        }
      }
      this.matchingBundleItemsWithQuantites.forEach(item => {
        const newQuantity = item.quantity + item.bundleQuantity
        payload.updates[item.key] = newQuantity
      })
      return payload
    },
    bundleDecreasePayload () {
      const payload = {
        updates: {
          [this.item.key]: this.item.quantity - 1
        }
      }
      this.matchingBundleItemsWithQuantites.forEach(item => {
        const newQuantity = item.quantity - item.bundleQuantity
        payload.updates[item.key] = newQuantity
      })
      return payload
    },
    bundlePrice () {
      if (this.isMetafieldsBundle) {
        return this.item.final_price
      }

      if (!this.isCustomisableBundle) {
        return null
      }

      let price = 0.00
      for (const [key, value] of Object.entries(this.item.properties)) {
        const matchingCartItem = this.matchingBundleItems.find(item => item.handle === value)
        price += matchingCartItem?.final_price
      }

      if (price === 0.00) {
        price = this.item.final_price
      }
      return price
    }
  },
  watch: {
    item () {
      if (this.customerHasExceededLimit) this.decreaseQuantity()
    }
  },
  mounted () {
    if (this.customerHasExceededLimit) this.decreaseQuantity()
  },
  methods: {
    ...mapActions(useCartStore, [
      'changeItems',
      'updateItems',
      'removeItem'
    ]),
    async increaseQuantity () {
      if (this.customerCanIncreaseQuantity) {
        this.updatingItem = true
        const newQuantity = this.item.quantity + 1
        const payload = {
          'id': this.item.key,
          'quantity': newQuantity
        }
        if (this.isCustomisableBundle) {
          await this.updateItems(this.bundleIncreasePayload)
        } else {
          await this.changeItems(payload)
        }
        this.updatingItem = false
      }
    },
    async decreaseQuantity () {
      this.updatingItem = true
      const newQuantity = this.item.quantity - 1
      const payload = {
        'id': this.item.key,
        'quantity': newQuantity
      }

      if (newQuantity === 0) {
        await this.removeItemFromCart()
      } else {
        if (this.isCustomisableBundle) {
          await this.updateItems(this.bundleDecreasePayload)
        } else {
          await this.changeItems(payload)
        }
      }
      this.updatingItem = false
    },
    async removeItemFromCart () {
      this.updatingItem = true

      if (this.isCustomisableBundle) {
        await this.updateItems(this.bundleRemovePayload)
      } else {
        await this.removeItem(this.item.key)
      }

      this.updatingItem = false
    }
  },
}
</script>
